import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from "@angular/core";
import { TooltipService } from "@directives/_misc/tooltip/tooltip.service";
import { IGoogleMapsMarker, IGoogleMapsMarkerOptions } from "@interfaces/IGoogleMapsMarkerOptions";
import { MapService } from "@services/map.service";
import * as angular from "angular";
import { ICommonMapComponent, VMapComponent } from "../v-map/v-map/v-map.component";

declare var MarkerWithLabel: any;
declare var MarkerClusterer: any;

export interface IMarkerModelMap {
    id: any;
    model: any,
    googleMarker: google.maps.Marker
}

@Component({
    selector: "ui-gmap-markers",
    template: ""
})
export class UIGmapMarkersComponent implements OnInit, OnChanges, OnDestroy {

    @Input() models: IGoogleMapsMarker[];
    @Output() onMarkersUpdated: EventEmitter<IMarkerModelMap[]> = new EventEmitter();

    @Input() idkey: string;
    @Input() coords: string;
    @Input() options: string;

    @Input() events: any;
    @Input() type: string;
    @Input() typeoptions: any;
    @Input() typeevents: any;

    @Input() showOutOfBoundsIndicator: boolean = false; // prototype functionality

    // ---

    // https://github.com/angular/angular/issues/24846#issuecomment-404452079

    veriMap: ICommonMapComponent;
    mapZoomListener: google.maps.MapsEventListener;

    markerModelMap: Map<any, IGoogleMapsMarker>;
    addedMarkerModelMap: Map<number, IMarkerModelMap> = new Map();
    markerClusterer: any;

    constructor(
        @Optional() private readonly vMap: VMapComponent,
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly mapService: MapService,
        private readonly tooltipService: TooltipService) {
    }

    ngOnInit(): void {
        this.veriMap = this.vMap || this.mapService.findVeriMap(this.elementRef);

        this.veriMap.mapInitialisedPromise.then(map => {

            this.mapZoomListener = this.veriMap.getMap().addListener("zoom_changed", () => {
                this.removeTooltips();
            });

            if (this.models && this.models.length != this.addedMarkerModelMap.size) {
                this.createMarkers();
            }

            const addedMarkerArray = this.getMarkerMapArray();
            const markerList = addedMarkerArray.map(map => map.googleMarker);

            this.markerClusterer = new MarkerClusterer(map, markerList, this.typeoptions);
            this.markerClusterer.setIgnoreHidden(true);

            if (this.typeevents && this.typeevents.postClusterShow) {
                this.markerClusterer.postClusterShow = (clusterIcon) => {
                    if (!clusterIcon.div_) {
                        return;
                    }

                    this.typeevents.postClusterShow(clusterIcon);
                }
            }

            // Disable if not setup
            if (this.type != "cluster") {
                this.markerClusterer.setMaxZoom(1);
                this.markerClusterer.setGridSize(1);
            }

            this.addClusterEvents();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["models"].currentValue == changes["models"].previousValue) {
            return;
        }

        if (this.idkey || this.coords || this.options) {
            console.error("Do not pass options into UIGmapMarkers", this.idkey, this.coords, this.options);
        }

        this.recreateMarkers();
    }

    ngOnDestroy(): void {

        this.mapZoomListener?.remove();

        // Clear existing listeners
        this.addedMarkerModelMap.forEach(marker => {
            this.removeMarkerEvents(marker.googleMarker);
            this.removeMarkerTooltip(marker.googleMarker);
        });

        if (this.markerClusterer) {
            google.maps.event.clearInstanceListeners(this.markerClusterer);
            this.markerClusterer.setMap(null);
            this.markerClusterer.clearMarkers();
        }

        this.models = [];
        this.addedMarkerModelMap.clear();
    }

    // ---------------------

    private removeTooltips(): void {

        if (!this.addedMarkerModelMap?.size) {
            return;
        }

        this.addedMarkerModelMap.forEach(marker => {
            this.removeMarkerTooltip(marker.googleMarker);
        });
    }

    private recreateMarkers() {
        if (this.markerClusterer && this.createMarkers()) {
            this.markerClusterer.repaint();
        }

        // V Map specific
        if (this.vMap) {
            const addedMarkerArray = this.getMarkerMapArray();
            this.vMap.fitMap(addedMarkerArray.map(m => m.googleMarker.getPosition()));
        }
    }

    private addClusterEvents() {

        if (this.typeevents == null) {
            return;
        }

        Object.keys(this.typeevents).forEach(eventName => {

            if (this.typeevents.hasOwnProperty(eventName) && angular.isFunction(this.typeevents[eventName])) {

                google.maps.event.addListener(this.markerClusterer, eventName, (mouseEvent) => {
                    this.typeevents[eventName](this.markerClusterer, eventName, mouseEvent);
                });
            }
        });
    }

    private addMarkerEvents(marker: google.maps.Marker, markerModel: any) {
        performance.mark("addMarkerEvents");

        if (this.events == null) {
            return;
        }

        // We want default mouse over option?
        google.maps.event.addListener(marker, "mouseover", (mouseEvent) => {
            if (!marker.getTitle()) {
                return;
            }

            this.removeMarkerTooltip(marker);

            const popupPosition = this.mapService.convertLatLngToPoint(marker.getPosition(), this.veriMap.getMap(), true);
            popupPosition.y -= 5;
            this.tooltipService.show(marker.getTitle(), { left: popupPosition.x, top: popupPosition.y, direction: "top" });
        });

        google.maps.event.addListener(marker, "mouseout", (mouseEvent) => {
            this.removeMarkerTooltip(marker);
        });

        google.maps.event.addListener(marker, "bounds_changed", (mouseEvent) => {
            this.removeMarkerTooltip(marker);
        });

        Object.keys(this.events).forEach(eventName => {
            if (this.events.hasOwnProperty(eventName) && angular.isFunction(this.events[eventName])) {

                google.maps.event.addListener(marker, eventName, (mouseEvent) => {
                    this.events[eventName](marker, eventName, markerModel, mouseEvent);
                });
            }
        });

        performance.measure("addMarkerEvents");
    }

    private removeMarkerTooltip(marker: google.maps.Marker) {
        this.tooltipService.hide();
    }

    private removeMarkerEvents(marker: google.maps.Marker) {
        if (!marker) {
            return;
        }

        google.maps.event.clearInstanceListeners(marker);
    }

    private getModel(model: IGoogleMapsMarker): IGoogleMapsMarker {
        return model["markerModel"] || model;
    }

    private createMarkers(): boolean {

        let numChanged = 0;

        this.createMarkerModelMap(this.models);

        performance.mark("createMarkers");

        for (let i = 0; i < this.models.length; i++) {
            const originalModel = this.models[i];
            const model = this.getModel(originalModel);

            if (!model) {
                return;
            }

            if (!model.options) {
                return;
            }
            if (typeof model.id == "undefined") {
                console.error("Marker must specify model ID", model);
                return;
            }

            if (model.options.labelAnchor) {
                model.options.labelAnchor = this.getLabelPositionPoint(model.options.labelAnchor) as any;
            }

            // Check for existing entry
            const existingMarkerMap = this.getExistingMarkerMap(model.id);

            if (existingMarkerMap) {

                if (this.markerNeedsUpdate(existingMarkerMap, model.options.position, model.options)) {

                    existingMarkerMap.googleMarker.setOptions(model.options as any);
                    existingMarkerMap.googleMarker.setPosition(new google.maps.LatLng(model.options.position.lat, model.options.position.lng));

                    numChanged++;
                }
            }
            else {
                this.addMarker(model, originalModel);

                numChanged++;
            }
        };

        performance.measure("createMarkers");

        const numRemoved = this.removeMarkers();

        if (this.onMarkersUpdated) {

            this.onMarkersUpdated.emit(this.getMarkerMapArray());
        }

        return numChanged !== 0 || numRemoved !== 0;
    }

    private markerNeedsUpdate(existingMarkerMap: IMarkerModelMap, coords: google.maps.LatLngLiteral, options: IGoogleMapsMarkerOptions): boolean {
        const marker = existingMarkerMap.googleMarker;

        // This can occasionally happen, unsure how
        if (!coords?.lat || !coords?.lng) {
            return true;
        }

        if (marker.getPosition().lat().toFixed(5) != coords.lat.toFixed(5) || marker.getPosition().lng().toFixed(5) != coords.lng.toFixed(5)) {
            return true;
        }

        if (marker.getVisible() != options.visible) {
            return true;
        }

        if (this.areIconsTheSame(marker.getIcon(), options.icon)) {
            return false;
        }

        return false;
    }

    private areIconsTheSame(icon1: string | google.maps.Icon | google.maps.Symbol, icon2: string | google.maps.Icon | google.maps.Symbol): boolean {

        if (typeof icon1 == "string") {
            return icon1 == icon2;
        }

        if ((icon1 as object).hasOwnProperty("url")) {
            const url1 = (icon1 as google.maps.Icon).url;
            const url2 = (icon2 as google.maps.Icon).url;

            return url1 == url2;
        }

        return false;
    }

    private addMarker(model: IGoogleMapsMarker, originalModel: IGoogleMapsMarker) {
        const marker = new MarkerWithLabel(model.options);

        marker.model = originalModel || model;

        this.addMarkerEvents(marker, model);

        if (this.markerClusterer) {
            // True to prevent draws
            this.markerClusterer.addMarker(marker, true);
        }

        this.addedMarkerModelMap.set(model.id, ({ id: model.id, googleMarker: marker, model: originalModel }));
    }

    private removeMarkers(): number {
        performance.mark("removeMarkers");

        const initialCount = this.addedMarkerModelMap.size;
        // Need to do the reverse now, find objects in our map that dont exist in polylines
        for (const [_, modelMap] of this.addedMarkerModelMap.entries()) {

            const model = this.getModelByKey(modelMap.id);

            if (!model) {
                this.markerModelMap.delete(modelMap.id);
                this.removeMarkerEvents(modelMap.googleMarker);
                this.removeMarkerTooltip(modelMap.googleMarker);
                this.markerClusterer?.removeMarker(modelMap.googleMarker, true);

                this.addedMarkerModelMap.delete(modelMap.id);
            }
        }

        performance.measure("removeMarkers");

        return Math.abs(initialCount - this.addedMarkerModelMap.size);
    }

    private getModelByKey(id: any) {
        return this.markerModelMap.get(id);
    }

    private getExistingMarkerMap(modelId: any): IMarkerModelMap {

        return this.addedMarkerModelMap.get(modelId);
    }

    private getLabelPositionPoint(anchor) {

        if (anchor instanceof google.maps.Point) {
            return anchor;
        }

        let xPos, yPos;

        if (anchor === void 0) {
            return void 0;
        }

        anchor = /^([-\d\.]+)\s([-\d\.]+)$/.exec(anchor);

        xPos = parseFloat(anchor[1]);
        yPos = parseFloat(anchor[2]);

        if (xPos && yPos) {
            return new google.maps.Point(xPos, yPos);
        }
    }

    private createMarkerModelMap(models: IGoogleMapsMarker[]) {
        this.markerModelMap = new Map(models?.map(m => {
            const model = this.getModel(m);
            return [model.id, model];
        }));
    }

    private getMarkerMapArray(): IMarkerModelMap[] {
        return Array.from(this.addedMarkerModelMap, ([_, value]) => value);
    }
}
